import React, { Suspense, useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, Html, Float, Center } from '@react-three/drei';
import * as THREE from 'three';
import Spinner from '@/components/common/Spinner';
import { Entity, Annotation, AnimationClip } from '../types';

class ModelErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <Html center>
          <div className="bg-white/90 backdrop-blur p-6 rounded-3xl shadow-xl text-center border-2 border-rose-100 max-w-[300px]">
            <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-4 font-black text-xl">!</div>
            <h4 className="font-black text-slate-900 mb-2">Lỗi tải mô hình 3D</h4>
            <p className="text-xs text-slate-500 leading-relaxed mb-4 text-pretty">Máy chủ lưu trữ mô hình 3D không phản hồi. Bạn có thể thử lại sau hoặc nhập chủ đề khác.</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-slate-900 text-white rounded-full text-xs font-black hover:bg-slate-800 transition-colors">Tải lại trang</button>
          </div>
        </Html>
      );
    }
    return this.props.children;
  }
}

interface ThreeViewerProps {
  modelUrl?: string;
  entities?: Entity[];
  annotations?: Annotation[];
  exploded?: boolean;
  sectionView?: boolean;
  onEntityClick?: (entityId: string) => void;
  activeEntityId?: string | null;
}

const Model = ({ url, exploded, sectionView, onEntityClick, activeEntityId }: any) => {
  // Use a very reliable official sample model from Khronos Group via JSDelivr CDN
  const defaultUrl = 'https://cdn.jsdelivr.net/gh/KhronosGroup/glTF-Sample-Models@master/2.0/Duck/glTF-Binary/Duck.glb';
  
  // Try loading the provided URL, fallback to default if it fails or is empty
  const { scene } = useGLTF(url || defaultUrl) as { scene: THREE.Group };
  const [clippingPlane] = useState(() => new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0));
  
  useEffect(() => {
    scene.traverse((child: any) => {
      if (child.isMesh) {
        child.material.clippingPlanes = sectionView ? [clippingPlane] : [];
        child.material.clipShadows = true;
        child.material.needsUpdate = true;
        
        // Highlight active entity
        if (activeEntityId && child.name.includes(activeEntityId)) {
          child.material.emissive = new THREE.Color(0x4f46e5);
          child.material.emissiveIntensity = 0.5;
        } else {
          child.material.emissive = new THREE.Color(0x000000);
          child.material.emissiveIntensity = 0;
        }
      }
    });
  }, [scene, sectionView, clippingPlane, activeEntityId]);

  useFrame(() => {
    if (exploded) {
      scene.traverse((child: any) => {
        if (child.isMesh) {
          // Simple explosion logic: move away from center
          const factor = 1.5;
          if (!child.userData.originalPos) {
            child.userData.originalPos = child.position.clone();
          }
          const direction = child.userData.originalPos.clone().normalize();
          child.position.lerp(child.userData.originalPos.clone().add(direction.multiplyScalar(factor)), 0.05);
        }
      });
    } else {
      scene.traverse((child: any) => {
        if (child.isMesh && child.userData.originalPos) {
          child.position.lerp(child.userData.originalPos, 0.05);
        }
      });
    }
  });

  return (
    <primitive 
      object={scene} 
      onClick={(e: any) => {
        e.stopPropagation();
        if (onEntityClick) onEntityClick(e.object.name);
      }} 
    />
  );
};

const Hotspot = ({ annotation }: { annotation: Annotation }) => {
  const [visible, setVisible] = useState(false);

  return (
    <group position={annotation.position}>
      <mesh onClick={() => setVisible(!visible)}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial color="#4f46e5" emissive="#4f46e5" emissiveIntensity={1} />
      </mesh>
      {visible && (
        <Html distanceFactor={10}>
          <div className="bg-white p-4 rounded-2xl shadow-2xl border border-indigo-100 min-w-[200px] animate-in zoom-in-95 duration-200">
            <h4 className="font-black text-slate-900 mb-1">{annotation.title}</h4>
            <p className="text-sm text-slate-600 leading-relaxed">{annotation.content}</p>
            {annotation.mediaUrl && (
              <div className="mt-3 rounded-xl overflow-hidden bg-slate-100 aspect-video">
                {/* Image or Video placeholder */}
                <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                  Media Content
                </div>
              </div>
            )}
            <button 
              className="mt-3 text-xs font-bold text-indigo-600 hover:text-indigo-700"
              onClick={() => setVisible(false)}
            >
              Đóng
            </button>
          </div>
        </Html>
      )}
    </group>
  );
};

export const ThreeViewer: React.FC<ThreeViewerProps> = ({
  modelUrl,
  entities,
  annotations = [],
  exploded = false,
  sectionView = false,
  onEntityClick,
  activeEntityId,
}) => {
  return (
    <div className="w-full h-[600px] rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-indigo-50/30 overflow-hidden relative border border-white shadow-inner">
      <Canvas 
        shadows 
        camera={{ position: [5, 5, 5], fov: 45 }}
        gl={{ localClippingEnabled: true }}
      >
        <Suspense fallback={<Html center><Spinner size="lg" /></Html>}>
          <ModelErrorBoundary>
            <Stage environment="city" intensity={0.5}>
              <Center>
                <Model 
                  url={modelUrl} 
                  exploded={exploded} 
                  sectionView={sectionView} 
                  onEntityClick={onEntityClick}
                  activeEntityId={activeEntityId}
                />
                {annotations.map((ann) => (
                  <Hotspot key={ann.id} annotation={ann} />
                ))}
              </Center>
            </Stage>
          </ModelErrorBoundary>
        </Suspense>
        <OrbitControls makeDefault />
      </Canvas>

      {/* Basic Controls UI Overlay */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-white/80 backdrop-blur-md px-6 py-3 rounded-full border border-white shadow-lg pointer-events-auto">
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">Xoay</span>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1" />
        </div>
        <div className="w-px h-6 bg-slate-200" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">Thu phóng</span>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1" />
        </div>
        <div className="w-px h-6 bg-slate-200" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">Di chuyển</span>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1" />
        </div>
      </div>
    </div>
  );
};
